import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Layers, Building2, Boxes, ArrowRight, Plus, Trash2, RefreshCw, Sparkles } from "lucide-react";
import BpmnEditorModal from "./BpmnEditorModal";
import LearningLoopPanel from "./LearningLoopPanel";
import ContentGovernancePanel from "./ContentGovernancePanel";
import PortfolioRegulatoryHeatmap from "./PortfolioRegulatoryHeatmap";
import ClientSpine from "./ClientSpine";
import ErpLensPanel from "./ErpLensPanel";
import TomLensPanel from "./TomLensPanel";
import BlueprintView from "./BlueprintView";
import ReportOverridesEditor from "./ReportOverridesEditor";
import InternalPricing from "./InternalPricing";
import CoBrandingEditor from "./CoBrandingEditor";
import DeliveryHub from "./DeliveryHub";
import ClientDocuments from "./ClientDocuments";
import EarlyWarningInbox from "./EarlyWarningInbox";
import NurtureSettingsCard from "./NurtureSettingsCard";
import ReassessmentPanel from "./ReassessmentPanel";
import { Eyebrow, Field, sel, inp, EMPTY_SYS } from "./e360Primitives";
import { OrgPicker } from "./OrgPicker";

export default function Enterprise360Panel() {
  const [view, setView] = useState("selector"); // selector | clients | curation
  const [catalogue, setCatalogue] = useState(null);
  const [entitlements, setEntitlements] = useState(null); // null = unknown → don't gate
  const [primary, setPrimary] = useState("#7737F6"); // white-label primary colour
  const [streamCounts, setStreamCounts] = useState({}); // published learnings per stream
  const [streamInsights, setStreamInsights] = useState({}); // published learning insights per stream
  const [profile, setProfile] = useState({ client_name: "", brand: { vision: "", values: "" } });
  const [systems, setSystems] = useState([]);
  const [blueprint, setBlueprint] = useState(null);
  const [busy, setBusy] = useState(false);

  // clients
  const [clients, setClients] = useState([]);
  const [consultants, setConsultants] = useState("");
  const [runIds, setRunIds] = useState("");
  const [org, setOrg] = useState(null);
  const [activeClient, setActiveClient] = useState(null);
  const [activeClientId, setActiveClientId] = useState(null);
  const [clientFocus, setClientFocus] = useState(null);
  const [editor, setEditor] = useState({ open: false, streamId: null, streamName: null });

  // ValueStream360 detailed assessment flow
  const [vsStep, setVsStep] = useState(1); // 1 foundational · 2 pick · 3 score · 4 result
  const [vsIndicators, setVsIndicators] = useState(null);
  const [vsScale, setVsScale] = useState([]);
  const [vsSelected, setVsSelected] = useState([]);
  const [vsAnswers, setVsAnswers] = useState({}); // { stream_id: { indicator_id: score } }
  const [vsMode, setVsMode] = useState("assess"); // assess | leads
  const [vsLeads, setVsLeads] = useState(null);
  const [vsSaveTarget, setVsSaveTarget] = useState("");

  const vsStreamScores = () => {
    const out = {};
    vsSelected.forEach((sid) => {
      const vals = Object.values(vsAnswers[sid] || {}).filter((v) => typeof v === "number");
      if (vals.length) out[sid] = vals.reduce((a, b) => a + b, 0) / vals.length;
    });
    return out;
  };

  const vsSaveToClient = async () => {
    if (!vsSaveTarget) { toast.error("Pick a client to save these scores to"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/enterprise360/clients/${vsSaveTarget}/value-stream-scores`,
        { scores: vsStreamScores() });
      toast.success(`Saved ${data.saved} value-stream score(s) — they now reinforce that client's blueprint`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save scores"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (view === "vs-assess" && !vsIndicators) {
      api.get("/admin/enterprise360/valuestream360/indicators")
        .then(({ data }) => { setVsIndicators(data.streams || []); setVsScale(data.scale || []); })
        .catch(() => toast.error("Could not load value-stream indicators"));
    }
  }, [view, vsIndicators]);

  useEffect(() => {
    if (view === "vs-assess" && vsMode === "leads" && vsLeads === null) {
      api.get("/admin/enterprise360/valuestream360/leads")
        .then(({ data }) => setVsLeads(data.leads || []))
        .catch(() => { setVsLeads([]); toast.error("Could not load leads"); });
    }
  }, [view, vsMode, vsLeads]);

  const vsStartFromLead = (lead) => {
    const sectorOpt = (catalogue?.dimensions || []).find((d) => d.id === "sector")?.options || [];
    setProfile((p) => ({
      ...p,
      client_name: lead.organisation || lead.full_name || "",
      sector: sectorOpt.includes((lead.sector || "").toLowerCase()) ? lead.sector.toLowerCase() : (p.sector || ""),
    }));
    setVsSelected(lead.streams.map((s) => s.id));
    setVsAnswers({});
    setBlueprint(null);
    setVsMode("assess");
    setVsStep(1);
    toast.success("Pre-loaded — complete the required foundational fields");
  };

  // One-click promote: LITE→Full. Opens the value-stream assessment wizard
  // pre-filled with the client's name + its self-declared streams, and pre-sets
  // the save target so the scored result flows straight back onto the client.
  useEffect(() => {
    const onPromote = (e) => {
      const d = e.detail || {};
      const c = clients.find((x) => x.id === d.clientId);
      setProfile((p) => ({ ...p, client_name: d.clientName || (c && c.client_name) || p.client_name,
        sector: (c && c.sector) || p.sector }));
      setVsSelected(Array.isArray(d.streamIds) ? d.streamIds : []);
      setVsAnswers({});
      setBlueprint(null);
      setVsMode("assess");
      setVsSaveTarget(d.clientId || "");
      setActiveClient(null);
      setView("vs-assess");
      setVsStep(1);
    };
    window.addEventListener("e360-promote-full", onPromote);
    return () => window.removeEventListener("e360-promote-full", onPromote);
  }, [clients]);

  useEffect(() => {
    api.get("/admin/enterprise360/selector")
      .then(({ data }) => {
        setCatalogue(data);
        const defaults = { client_name: "" };
        data.dimensions.forEach((d) => { if (d.options && !d.required) defaults[d.id] = d.options[0]; });
        setProfile((p) => ({ ...defaults, ...p, brand: { vision: "", values: "" } }));
      })
      .catch(() => toast.error("Could not load the Enterprise360 selector"));
  }, []);

  const loadClients = useCallback(() => {
    api.get("/admin/enterprise360/clients").then(({ data }) => setClients(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { if (view === "clients" || view === "curation" || view === "vs-assess") loadClients(); }, [view, loadClients]);

  useEffect(() => {
    api.get("/admin/instance/config")
      .then(({ data }) => {
        setEntitlements(data.entitlements || []);
        setPrimary(data.branding?.primary_color || "#7737F6");
      })
      .catch(() => setEntitlements(null)); // unknown → don't gate
  }, []);

  const loadStreamCounts = useCallback(() => {
    api.get("/admin/enterprise360/learning/stream-counts")
      .then(({ data }) => setStreamCounts(data.counts || {})).catch(() => {});
    api.get("/admin/enterprise360/learning/published-by-stream")
      .then(({ data }) => setStreamInsights(data.by_stream || {})).catch(() => {});
  }, []);
  useEffect(() => { loadStreamCounts(); }, [loadStreamCounts]);

  const entitled = useCallback((key) => (entitlements ? entitlements.includes(key) : true), [entitlements]);

  const buildProfilePayload = () => ({
    ...profile,
    brand: { vision: profile.brand?.vision || "", mission: "",
             values: (profile.brand?.values || "").split(",").map((s) => s.trim()).filter(Boolean) },
    application_landscape: systems.filter((s) => s.name).map((s) => ({
      name: s.name, category: s.category || null,
      business_fit: Number(s.business_fit), technical_health: Number(s.technical_health),
      serves: (s.serves || "").split(",").map((x) => x.trim()).filter(Boolean),
    })),
  });

  const preview = async () => {
    if (!profile.client_name) { toast.error("Enter a client name"); return; }
    const missing = requiredMissing();
    if (missing.length) { toast.error("Required: " + missing.join(", ")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/enterprise360/blueprint", buildProfilePayload());
      setBlueprint(data);
      toast.success("Blueprint assembled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Assembly failed"); }
    finally { setBusy(false); }
  };

  const selectE360Org = (o) => {
    setOrg(o);
    if (!o) return;
    // Prefill the profile from the canonical org — consultant can override any field.
    setProfile((p) => {
      const next = { ...p };
      if (o.legal_name && !next.client_name) next.client_name = o.legal_name;
      const carry = ["sector", "geography", "regulation", "supply_chain_model",
                     "customer_model", "product_model", "operating_model", "ambition"];
      carry.forEach((f) => { if (o[f]) next[f] = o[f]; });
      if (o.annual_value_base_gbp != null) next.annual_value_base_gbp = o.annual_value_base_gbp;
      return next;
    });
    toast.success("Profile prefilled from organisation — override any field as needed");
  };

  const saveClient = async () => {
    if (!profile.client_name) { toast.error("Enter a client name"); return; }
    setBusy(true);
    try {
      await api.post("/admin/enterprise360/clients", {
        profile: buildProfilePayload(),
        consultant_emails: consultants.split(",").map((s) => s.trim()).filter(Boolean),
        linked_run_ids: runIds.split(",").map((s) => s.trim()).filter(Boolean),
        organisation_id: org?.id || null,
      });
      toast.success("Client engagement created");
      setView("clients"); loadClients();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create client"); }
    finally { setBusy(false); }
  };

  const vsToggleStream = (sid) =>
    setVsSelected((prev) => prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]);

  const vsSetAnswer = (sid, indId, score) =>
    setVsAnswers((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), [indId]: score } }));

  const vsStreamAnswered = (sid) => {
    const ind = (vsIndicators || []).find((s) => s.id === sid);
    const ans = vsAnswers[sid] || {};
    return ind ? ind.indicators.every((q) => ans[q.id] != null) : false;
  };

  const requiredMissing = () =>
    (catalogue?.dimensions || []).filter((d) => d.required && !profile[d.id]).map((d) => d.label);

  const vsAssemble = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/enterprise360/valuestream360/assess", {
        profile: buildProfilePayload(),
        selected_streams: vsSelected,
        stream_answers: vsAnswers,
      });
      setBlueprint(data); setVsStep(4);
      toast.success("Focused blueprint assembled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Assembly failed"); }
    finally { setBusy(false); }
  };

  const openClientBlueprint = async (id, focus = null) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/enterprise360/clients/${id}/blueprint`);
      setActiveClient(data); setActiveClientId(id); setBlueprint(data); setClientFocus(focus);
    } catch { toast.error("Could not load joined-up blueprint"); }
    finally { setBusy(false); }
  };

  const addSystem = () => setSystems((s) => [...s, { ...EMPTY_SYS }]);
  const updSystem = (i, k, v) => setSystems((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const delSystem = (i) => setSystems((s) => s.filter((_, j) => j !== i));

  if (!catalogue) return <div className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]">Loading…</div>;

  return (
    <div data-testid="enterprise360-panel" className="space-y-8" style={{ "--e360-primary": primary }}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Eyebrow>ENTERPRISE360 · Reinvention Blueprint Engine</Eyebrow>
          <h2 className="text-2xl font-semibold ek-text dark:text-[var(--ek-dark-text)]">Fit-for-purpose world-class blueprint</h2>
          <p className="text-sm ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-1">Profile a client, resolve the archetype, and assemble the tailored blueprint — fed by existing EKSELENS360 assessments where they exist.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="e360-view-selector" onClick={() => setView("selector")}
            className={`px-4 py-2 text-[12px] tracking-[0.14em] uppercase border ${view === "selector" ? "border-[var(--e360-primary)] ek-text dark:text-[var(--ek-dark-text)]" : "ek-line dark:border-[var(--ek-dark-border)] ek-text-muted dark:text-[var(--ek-dark-text-muted)]"}`}>Blueprint Selector</button>
          <button data-testid="e360-view-clients" onClick={() => setView("clients")}
            className={`px-4 py-2 text-[12px] tracking-[0.14em] uppercase border ${view === "clients" ? "border-[var(--e360-primary)] ek-text dark:text-[var(--ek-dark-text)]" : "ek-line dark:border-[var(--ek-dark-border)] ek-text-muted dark:text-[var(--ek-dark-text-muted)]"}`}>Clients</button>
          <button data-testid="e360-view-vs-assess" onClick={() => setView("vs-assess")}
            className={`px-4 py-2 text-[12px] tracking-[0.14em] uppercase border flex items-center gap-1 ${view === "vs-assess" ? "border-[var(--e360-primary)] ek-text dark:text-[var(--ek-dark-text)]" : "ek-line dark:border-[var(--ek-dark-border)] ek-text-muted dark:text-[var(--ek-dark-text-muted)]"}`}>
            <Sparkles size={13} /> ValueStream360</button>
          {entitled("learning_loop") && (
            <button data-testid="e360-view-curation" onClick={() => setView("curation")}
              className={`px-4 py-2 text-[12px] tracking-[0.14em] uppercase border flex items-center gap-1 ${view === "curation" ? "border-[var(--e360-primary)] ek-text dark:text-[var(--ek-dark-text)]" : "ek-line dark:border-[var(--ek-dark-border)] ek-text-muted dark:text-[var(--ek-dark-text-muted)]"}`}>
              <Sparkles size={13} /> Curation
            </button>
          )}
        </div>
      </div>

      {view === "clients" && !activeClient && (
        <div className="space-y-4">
          <NurtureSettingsCard />
          <EarlyWarningInbox onOpenClient={openClientBlueprint} />
          <PortfolioRegulatoryHeatmap onOpenClient={openClientBlueprint} />
          <div className="flex items-center justify-between">
            <Eyebrow>Joined-up client engagements</Eyebrow>
            <button onClick={loadClients} className="ek-text-muted dark:text-[var(--ek-dark-text-muted)] hover:text-black"><RefreshCw size={16} /></button>
          </div>
          {clients.length === 0 ? (
            <p className="text-sm ek-text-muted dark:text-[var(--ek-dark-text-muted)]">No client engagements yet. Build a profile in the Blueprint Selector and save it as a client.</p>
          ) : (
            <div className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg divide-y">
              {clients.map((c) => (
                <div key={c.id} data-testid={`e360-client-${c.id}`} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <div className="font-medium ek-text dark:text-[var(--ek-dark-text)] flex items-center gap-2">
                      {c.client_name}
                      {c.health && (
                        <span data-testid={`e360-client-health-${c.id}`}
                          title={`${c.health.reds} red · ${c.health.ambers} amber · ${c.health.greens} green`}
                          className={`text-[10px] rounded-full px-2 py-0.5 border font-semibold ${
                            c.health.overall === "red" ? "text-red-700 bg-red-50 border-red-200"
                            : c.health.overall === "amber" ? "text-amber-700 bg-amber-50 border-amber-200"
                            : "text-emerald-700 bg-emerald-50 border-emerald-200"}`}>
                          ● {c.health.overall === "green" ? "Ready" : c.health.overall === "amber" ? "Amber" : "Red"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs ek-text-muted dark:text-[var(--ek-dark-text-muted)]">{c.sector} · {c.consultants} consultant(s) · {c.linked_runs} linked assessment(s)</div>
                  </div>
                  <button data-testid={`e360-open-${c.id}`} onClick={() => openClientBlueprint(c.id)}
                    className="text-[12px] tracking-[0.14em] uppercase text-[var(--e360-primary)] flex items-center gap-1">
                    Joined-up blueprint <ArrowRight size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "selector" && (
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Selector form */}
          <div className="space-y-4">
            <Eyebrow><Layers size={13} className="inline mr-1" /> Blueprint Selector</Eyebrow>
            {(() => {
              const secDim = (catalogue.dimensions || []).find((d) => d.id === "sector");
              const sectorCount = secDim ? (secDim.options || []).filter((o) => o !== "other").length : 0;
              const archCount = (catalogue.archetypes || []).length;
              return (
                <div data-testid="e360-coverage-badge" className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase">
                  <span className="inline-flex items-center gap-1 bg-[var(--e360-primary)]/10 text-[var(--e360-primary)] rounded-full px-3 py-1">
                    <Layers size={12} /> {sectorCount} sectors
                  </span>
                  <span className="inline-flex items-center gap-1 ek-surface-2 dark:bg-[var(--ek-dark-surface-hi)] ek-text-muted dark:text-[var(--ek-dark-text-muted)] rounded-full px-3 py-1">
                    <Building2 size={12} /> {archCount} archetypes
                  </span>
                </div>
              );
            })()}
            <Field label="Client name">
              <input data-testid="e360-client-name" className={inp} value={profile.client_name || ""}
                onChange={(e) => setProfile({ ...profile, client_name: e.target.value })} placeholder="e.g. Acme Global" />
            </Field>
            <div className="mt-1">
              <OrgPicker selected={org} onSelect={selectE360Org} seedName={profile.client_name}
                label="Link canonical organisation" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {catalogue.dimensions.map((d) => (
                <div key={d.id} className={d.full_width ? "sm:col-span-2" : ""}>
                  <Field label={d.label + (d.required ? " *" : "")}>
                    {d.options ? (
                      <select data-testid={`e360-dim-${d.id}`} className={sel} value={profile[d.id] || ""}
                        onChange={(e) => setProfile({ ...profile, [d.id]: e.target.value })}>
                        {d.required && <option value="">—</option>}
                        {d.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
                      </select>
                    ) : d.textarea ? (
                      <textarea data-testid={`e360-dim-${d.id}`} rows={4} className={`${inp} resize-y`} value={profile[d.id] || ""}
                        onChange={(e) => setProfile({ ...profile, [d.id]: e.target.value })} placeholder={d.help || ""} />
                    ) : (
                      <input data-testid={`e360-dim-${d.id}`} className={inp} value={profile[d.id] || ""}
                        onChange={(e) => setProfile({ ...profile, [d.id]: e.target.value })} placeholder={d.help || ""} />
                    )}
                  </Field>
                </div>
              ))}
            </div>
            <Field label="Client vision (brand lens)">
              <input className={inp} value={profile.brand?.vision || ""}
                onChange={(e) => setProfile({ ...profile, brand: { ...profile.brand, vision: e.target.value } })}
                placeholder="Their stated ambition — output is calibrated to this" />
            </Field>
            <Field label="Brand values (comma-separated)">
              <input className={inp} value={profile.brand?.values || ""}
                onChange={(e) => setProfile({ ...profile, brand: { ...profile.brand, values: e.target.value } })} />
            </Field>
            <Field label="Annual value base (£) — for indicative white-space opportunity">
              <input data-testid="e360-value-base" type="number" className={inp} value={profile.annual_value_base_gbp || ""}
                onChange={(e) => setProfile({ ...profile, annual_value_base_gbp: e.target.value ? Number(e.target.value) : null })}
                placeholder="e.g. 100000000" />
            </Field>

            {/* Application landscape */}
            <div className="border-t ek-line dark:border-[var(--ek-dark-border)] pt-3">
              <div className="flex items-center justify-between mb-2">
                <Eyebrow><Boxes size={13} className="inline mr-1" /> Incumbent systems (TIME disposition)</Eyebrow>
                <button data-testid="e360-add-system" onClick={addSystem} className="text-[var(--e360-primary)] flex items-center gap-1 text-xs"><Plus size={14} /> Add system</button>
              </div>
              {systems.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
                  <input className={`${inp} col-span-3`} placeholder="Name" value={s.name} onChange={(e) => updSystem(i, "name", e.target.value)} />
                  <input className={`${inp} col-span-2`} placeholder="ERP/CRM…" value={s.category} onChange={(e) => updSystem(i, "category", e.target.value)} />
                  <input className={`${inp} col-span-2`} placeholder="serves" value={s.serves} onChange={(e) => updSystem(i, "serves", e.target.value)} />
                  <select className={`${sel} col-span-2`} value={s.business_fit} onChange={(e) => updSystem(i, "business_fit", e.target.value)} title="Business fit">
                    {[1,2,3,4,5].map((n) => <option key={n} value={n}>fit {n}</option>)}
                  </select>
                  <select className={`${sel} col-span-2`} value={s.technical_health} onChange={(e) => updSystem(i, "technical_health", e.target.value)} title="Technical health">
                    {[1,2,3,4,5].map((n) => <option key={n} value={n}>tech {n}</option>)}
                  </select>
                  <button className="col-span-1 text-rose-500 flex justify-center" onClick={() => delSystem(i)}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>

            {/* Continuity inputs — link this profile to consultants & existing assessments */}
            <div className="border-t ek-line dark:border-[var(--ek-dark-border)] pt-3 grid sm:grid-cols-2 gap-3">
              <Field label="Consultant emails (comma-separated)">
                <input data-testid="e360-consultants" className={inp} placeholder="alex@firm.com, jo@firm.com"
                  value={consultants} onChange={(e) => setConsultants(e.target.value)} />
              </Field>
              <Field label="Link existing assessment run IDs (comma-separated)">
                <input data-testid="e360-run-ids" className={inp} placeholder="run_abc123, run_def456"
                  value={runIds} onChange={(e) => setRunIds(e.target.value)} />
              </Field>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <button data-testid="e360-assemble" onClick={preview} disabled={busy}
                className="bg-[var(--e360-primary)] text-white px-6 py-2.5 text-[12px] tracking-[0.16em] uppercase disabled:opacity-50">
                {busy ? "Assembling…" : "Assemble blueprint"}
              </button>
              <button data-testid="e360-save-client" onClick={saveClient} disabled={busy}
                className="border border-[var(--e360-primary)] text-[var(--e360-primary)] px-6 py-2.5 text-[12px] tracking-[0.16em] uppercase disabled:opacity-50">
                Save as client
              </button>
            </div>
          </div>

          {/* Preview output */}
          <div className="lg:sticky lg:top-24 self-start">
            {blueprint ? <BlueprintView bp={blueprint} streamCounts={streamCounts} streamInsights={streamInsights} /> :
              <div className="border border-dashed ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-8 text-center ek-text-muted dark:text-[var(--ek-dark-text-muted)] text-sm">
                Assemble a blueprint to preview the archetype, overlays, capability heatmap and tailored function blueprints.
              </div>}
          </div>
        </div>
      )}

      {view === "curation" && entitled("learning_loop") && (
        <>
          <LearningLoopPanel clients={clients} />
          <ContentGovernancePanel />
        </>
      )}

      {view === "vs-assess" && (
        <div data-testid="e360-vs-assess">
          {/* Mode toggle: new assessment vs inbound leads */}
          <div className="flex gap-2 mb-6">
            <button data-testid="vs-mode-assess" onClick={() => setVsMode("assess")}
              className={`px-3 py-1.5 text-[12px] rounded border ${vsMode === "assess" ? "border-[var(--e360-primary)] ek-text dark:text-[var(--ek-dark-text)]" : "ek-line dark:border-[var(--ek-dark-border)] ek-text-muted dark:text-[var(--ek-dark-text-muted)]"}`}>New assessment</button>
            <button data-testid="vs-mode-leads" onClick={() => setVsMode("leads")}
              className={`px-3 py-1.5 text-[12px] rounded border ${vsMode === "leads" ? "border-[var(--e360-primary)] ek-text dark:text-[var(--ek-dark-text)]" : "ek-line dark:border-[var(--ek-dark-border)] ek-text-muted dark:text-[var(--ek-dark-text-muted)]"}`}>
              Inbound leads{Array.isArray(vsLeads) && vsLeads.length ? ` (${vsLeads.length})` : ""}</button>
          </div>

          {vsMode === "leads" && (
            <div data-testid="vs-leads-list">
              <Eyebrow>Inbound ValueStream360 leads</Eyebrow>
              <p className="text-sm ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-4">Prospects who self-assessed value streams on the website. Pre-load one into the wizard to run a consultant-grade detailed assessment.</p>
              {vsLeads === null ? <p className="ek-text-muted dark:text-[var(--ek-dark-text-muted)] text-sm">Loading…</p> :
                vsLeads.length === 0 ? <p className="ek-text-muted dark:text-[var(--ek-dark-text-muted)] text-sm" data-testid="vs-leads-empty">No value-stream leads yet.</p> : (
                <div className="space-y-3">
                  {vsLeads.map((l) => (
                    <div key={l.id} className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`vs-lead-${l.id}`}>
                      <div>
                        <div className="text-[15px] font-medium ek-text dark:text-[var(--ek-dark-text)]">{l.full_name || l.email}{l.organisation ? ` · ${l.organisation}` : ""}</div>
                        <div className="text-[12px] ek-text-muted dark:text-[var(--ek-dark-text-muted)]">{l.email}{l.sector ? ` · ${l.sector}` : ""}{l.role ? ` · ${l.role}` : ""}</div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {l.streams.map((s) => (
                            <span key={s.id} className="text-[11px] bg-[var(--e360-primary)]/8 text-[var(--e360-primary)] border border-[var(--e360-primary)]/20 rounded-full px-2.5 py-0.5">
                              {s.name}{s.overall != null ? ` · ${s.overall}/4` : ""}
                            </span>
                          ))}
                        </div>
                        {l.note && <div className="text-[12px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-2 italic">“{l.note}”</div>}
                      </div>
                      <button data-testid={`vs-lead-start-${l.id}`} onClick={() => vsStartFromLead(l)}
                        className="ek-btn ek-btn-primary shrink-0 text-[13px]">Start assessment <ArrowRight size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {vsMode === "assess" && (<>
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6 text-[11px] tracking-[0.14em] uppercase">
            {["Foundational data", "Pick value streams", "Assess maturity", "Focused blueprint"].map((s, i) => (
              <div key={s} className={`flex items-center gap-2 ${vsStep === i + 1 ? "text-[var(--e360-primary)]" : "ek-text-muted dark:text-[var(--ek-dark-text-muted)]"}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center border ${vsStep >= i + 1 ? "border-[var(--e360-primary)] bg-[var(--e360-primary)]/10" : "ek-line dark:border-[var(--ek-dark-border)]"}`}>{i + 1}</span>
                <span className="hidden md:inline">{s}</span>
                {i < 3 && <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)] ml-1">→</span>}
              </div>
            ))}
          </div>

          {/* Step 1 — foundational data (the gate) */}
          {vsStep === 1 && (
            <div className="max-w-3xl" data-testid="vs-step-foundational">
              <Eyebrow>Step 1 · Foundational data</Eyebrow>
              <p className="text-sm ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-4">Capture the essentials so the engine can classify the business correctly. <span className="text-[var(--e360-primary)]">*</span> fields are required.</p>
              <Field label="Client name *">
                <input data-testid="vs-client-name" className={inp} value={profile.client_name || ""}
                  onChange={(e) => setProfile({ ...profile, client_name: e.target.value })} placeholder="e.g. Acme Global" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {(catalogue.dimensions || []).map((d) => (
                  <div key={d.id} className={d.full_width ? "sm:col-span-2" : ""}>
                    <Field label={d.label + (d.required ? " *" : "")}>
                      {d.options ? (
                        <select data-testid={`vs-dim-${d.id}`} className={sel} value={profile[d.id] || ""}
                          onChange={(e) => setProfile({ ...profile, [d.id]: e.target.value })}>
                          <option value="">—</option>
                          {d.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
                        </select>
                      ) : d.textarea ? (
                        <textarea data-testid={`vs-dim-${d.id}`} rows={3} className={`${inp} resize-y`} value={profile[d.id] || ""}
                          onChange={(e) => setProfile({ ...profile, [d.id]: e.target.value })} placeholder={d.help || ""} />
                      ) : (
                        <input data-testid={`vs-dim-${d.id}`} className={inp} value={profile[d.id] || ""}
                          onChange={(e) => setProfile({ ...profile, [d.id]: e.target.value })} placeholder={d.help || ""} />
                      )}
                    </Field>
                  </div>
                ))}
              </div>
              <button data-testid="vs-step1-next" disabled={busy}
                onClick={() => {
                  if (!profile.client_name) { toast.error("Enter a client name"); return; }
                  const missing = requiredMissing();
                  if (missing.length) { toast.error("Required: " + missing.join(", ")); return; }
                  setVsStep(2);
                }}
                className="ek-btn ek-btn-primary mt-6 inline-flex items-center gap-2">
                Pick value streams <ArrowRight size={16} />
              </button>
            </div>
          )}

          {/* Step 2 — pick streams */}
          {vsStep === 2 && (
            <div data-testid="vs-step-pick">
              <Eyebrow>Step 2 · Which value streams?</Eyebrow>
              <p className="text-sm ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-4">Start with the 2–3 that matter most — you can expand to the whole enterprise later.</p>
              {!vsIndicators ? <p className="ek-text-muted dark:text-[var(--ek-dark-text-muted)] text-sm">Loading…</p> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {vsIndicators.map((s) => {
                    const on = vsSelected.includes(s.id);
                    return (
                      <button key={s.id} type="button" onClick={() => vsToggleStream(s.id)}
                        data-testid={`vs-pick-${s.id}`}
                        className={`text-left border rounded-lg p-4 transition-colors ${on ? "border-[var(--e360-primary)] bg-[var(--e360-primary)]/5" : "ek-line dark:border-[var(--ek-dark-border)] hover:border-zinc-300"}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-[15px] font-medium ek-text dark:text-[var(--ek-dark-text)]">{s.name}</span>
                          <span className={`w-4 h-4 rounded border ${on ? "bg-[var(--e360-primary)] border-[var(--e360-primary)]" : "ek-line dark:border-[var(--ek-dark-border)]"}`} />
                        </div>
                        <div className="text-[11px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] mt-1">{s.is_enabling_layer ? "Enabling layer" : "Core value stream"} · {s.indicators.length} questions</div>
                        {s.specialist_lenses?.length > 0 && <div className="text-[10px] text-[var(--e360-primary)] mt-1">{s.specialist_lenses.join(" · ")}</div>}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-3 mt-6">
                <button onClick={() => setVsStep(1)} className="text-[12px] tracking-[0.14em] uppercase ek-text-muted dark:text-[var(--ek-dark-text-muted)]">← Back</button>
                <button data-testid="vs-step2-next" disabled={!vsSelected.length}
                  onClick={() => setVsStep(3)}
                  className="ek-btn ek-btn-primary inline-flex items-center gap-2 disabled:opacity-40">
                  Assess {vsSelected.length || ""} stream{vsSelected.length === 1 ? "" : "s"} <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — score */}
          {vsStep === 3 && (
            <div data-testid="vs-step-score">
              <Eyebrow>Step 3 · Maturity</Eyebrow>
              <p className="text-sm ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-5">Score each indicator. These are the same authored diagnostics the engine uses, so your scores feed straight into the capability heatmap.</p>
              <div className="space-y-6">
                {vsSelected.map((sid) => {
                  const s = (vsIndicators || []).find((x) => x.id === sid);
                  if (!s) return null;
                  return (
                    <div key={sid} className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-5" data-testid={`vs-score-${sid}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="ek-heading text-lg ek-text dark:text-[var(--ek-dark-text)]">{s.name}</h4>
                        {vsStreamAnswered(sid) && <span className="text-[11px] text-emerald-700">✓ complete</span>}
                      </div>
                      <div className="space-y-3">
                        {s.indicators.map((q) => (
                          <div key={q.id} data-testid={`vs-q-${q.id}`}>
                            <div className="text-[13.5px] ek-text dark:text-[var(--ek-dark-text)] mb-1.5">{q.prompt}</div>
                            <div className="flex flex-wrap gap-1.5">
                              {vsScale.map((opt) => {
                                const on = (vsAnswers[sid] || {})[q.id] === opt.score;
                                return (
                                  <button key={opt.score} type="button" onClick={() => vsSetAnswer(sid, q.id, opt.score)}
                                    data-testid={`vs-q-${q.id}-opt-${opt.score}`}
                                    title={opt.label}
                                    className={`text-[12px] px-3 py-1.5 rounded border ${on ? "bg-[var(--e360-primary)] text-white border-[var(--e360-primary)]" : "ek-line dark:border-[var(--ek-dark-border)] ek-text-muted dark:text-[var(--ek-dark-text-muted)] hover:border-[var(--e360-primary)]"}`}>
                                    {opt.score} · {opt.label.split(" — ")[0]}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setVsStep(2)} className="text-[12px] tracking-[0.14em] uppercase ek-text-muted dark:text-[var(--ek-dark-text-muted)]">← Back</button>
                <button data-testid="vs-assemble" disabled={busy || !vsSelected.every(vsStreamAnswered)}
                  onClick={vsAssemble}
                  className="ek-btn ek-btn-primary inline-flex items-center gap-2 disabled:opacity-40">
                  {busy ? "Assembling…" : "Assemble focused blueprint"} <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Step 4 — focused blueprint */}
          {vsStep === 4 && blueprint && (
            <div data-testid="vs-step-result">
              <div className="flex items-center justify-between mb-4">
                <Eyebrow>Focused Reinvention Blueprint · {blueprint.active_value_streams?.length} stream(s)</Eyebrow>
                <button data-testid="vs-restart" onClick={() => { setVsStep(2); setBlueprint(null); }}
                  className="text-[12px] tracking-[0.14em] uppercase ek-text-muted dark:text-[var(--ek-dark-text-muted)]">Adjust streams</button>
              </div>
              <div className="border border-[var(--e360-primary)]/30 bg-[var(--e360-primary)]/5 rounded-lg p-4 mb-5 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="vs-save-to-client">
                <div className="flex-1">
                  <div className="text-[11px] tracking-[0.14em] uppercase text-[var(--e360-primary)] mb-1">Save scores to a client engagement</div>
                  <p className="text-[12px] ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-2">Reinforce that client's joined-up blueprint &amp; Maturity Spine with these value-stream self-scores.</p>
                  <select data-testid="vs-save-client-select" className={sel} value={vsSaveTarget}
                    onChange={(e) => setVsSaveTarget(e.target.value)}>
                    <option value="">Select a client…</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
                  </select>
                </div>
                <button data-testid="vs-save-scores" onClick={vsSaveToClient} disabled={busy || !vsSaveTarget}
                  className="ek-btn ek-btn-primary shrink-0 disabled:opacity-40">Save scores</button>
              </div>
              {blueprint.maturity_deltas && Object.keys(blueprint.maturity_deltas).length > 0 && (
                <div className="border ek-line dark:border-[var(--ek-dark-border)] rounded-lg p-4 mb-5" data-testid="vs-maturity-delta">
                  <div className="text-[11px] tracking-[0.16em] uppercase ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-3">Start vs target maturity</div>
                  <div className="space-y-2.5">
                    {Object.entries(blueprint.maturity_deltas).map(([sid, d]) => {
                      const name = (blueprint.function_blueprints.find((f) => f.id === sid) || {}).name || sid;
                      const pct = (v) => `${(v / 4) * 100}%`;
                      return (
                        <div key={sid} data-testid={`vs-delta-${sid}`}>
                          <div className="flex justify-between text-[12.5px] mb-1">
                            <span className="ek-text dark:text-[var(--ek-dark-text)] font-medium">{name}</span>
                            <span className="ek-text-muted dark:text-[var(--ek-dark-text-muted)]">{d.current} ({d.current_band}) → <span className="text-[var(--e360-primary)]">{d.target} ({d.target_band})</span> · gap {d.gap}</span>
                          </div>
                          <div className="relative h-2 ek-surface-2 dark:bg-[var(--ek-dark-surface-hi)] rounded-full">
                            <div className="absolute h-2 bg-[var(--e360-primary)]/25 rounded-full" style={{ width: pct(d.target) }} />
                            <div className="absolute h-2 bg-[var(--e360-primary)] rounded-full" style={{ width: pct(d.current) }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <BlueprintView bp={blueprint} qaProfile={buildProfilePayload()} streamCounts={streamCounts} streamInsights={streamInsights} />
            </div>
          )}
          </>)}
        </div>
      )}

      {view === "clients" && activeClient && (
        <div>
          <button onClick={() => { setActiveClient(null); setActiveClientId(null); setBlueprint(null); setClientFocus(null); }} className="text-[12px] tracking-[0.14em] uppercase ek-text-muted dark:text-[var(--ek-dark-text-muted)] mb-4">← Back to clients</button>
          <ClientSpine clientId={activeClientId} />
          <ReassessmentPanel clientId={activeClientId} />
          <div className="mb-6"><ErpLensPanel clientId={activeClientId} /></div>
          <TomLensPanel clientId={activeClientId} />
          <DeliveryHub clientId={activeClientId} focus={clientFocus} />
          <ClientDocuments clientId={activeClientId} />
          <CoBrandingEditor clientId={activeClientId} logoUrl={activeClient.client_logo_url}
            roster={activeClient.engagement_roster} onSaved={() => openClientBlueprint(activeClientId)} />
          <InternalPricing clientId={activeClientId} />
          <ReportOverridesEditor clientId={activeClientId} sections={activeClient.report_sections}
            onSaved={() => openClientBlueprint(activeClientId)} />
          <BlueprintView bp={activeClient} kitClientId={activeClientId} showKit={entitled("kit_export")} streamCounts={streamCounts} streamInsights={streamInsights}
            onEditMap={(sid, nm) => setEditor({ open: true, streamId: sid, streamName: nm })} />
        </div>
      )}

      <BpmnEditorModal open={editor.open} clientId={activeClientId} streamId={editor.streamId}
        streamName={editor.streamName} onClose={() => setEditor({ open: false, streamId: null, streamName: null })}
        onSaved={() => activeClientId && openClientBlueprint(activeClientId)} />
    </div>
  );
}
